import React from "react";
import {
  BarChart3,
  ShoppingCart,
  Users,
  Settings,
  Clock,
  Shield,
  ArrowRight
} from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";

interface Feature {
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  color: string;
}

const features: Feature[] = [
  {
    icon: <ShoppingCart className="h-6 w-6 text-blue-600" />,
    titleKey: "features_inventory_title",
    descriptionKey: "features_inventory_description",
    color: "bg-blue-100",
  },
  {
    icon: <BarChart3 className="h-6 w-6 text-indigo-600" />,
    titleKey: "features_analytics_title",
    descriptionKey: "features_analytics_description",
    color: "bg-indigo-100",
  },
  {
    icon: <Users className="h-6 w-6 text-emerald-600" />,
    titleKey: "features_customer_title",
    descriptionKey: "features_customer_description",
    color: "bg-emerald-100",
  },
  {
    icon: <Settings className="h-6 w-6 text-amber-600" />,
    titleKey: "features_integration_title",
    descriptionKey: "features_integration_description",
    color: "bg-amber-100",
  },
  {
    icon: <Clock className="h-6 w-6 text-rose-600" />,
    titleKey: "features_updates_title",
    descriptionKey: "features_updates_description",
    color: "bg-rose-100",
  },
  {
    icon: <Shield className="h-6 w-6 text-purple-600" />,
    titleKey: "features_security_title",
    descriptionKey: "features_security_description",
    color: "bg-purple-100",
  },
];

const FeaturesSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-white relative" id="features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold mb-4 border border-indigo-100">
              {t("features_badge")}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
              {t("features_title_part1")}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                {t("features_title_part2")}
              </span>
            </h2>
            <p className="text-lg text-slate-600">
              {t("features_description")}
            </p>
          </div>
          
          <a href="#solutions" className="hidden md:flex items-center font-bold text-primary hover:text-primary-dark transition-colors group">
            {t("features_button_exploreSolutions")}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-8 bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-enterprise transition-all duration-300 group hover:-translate-y-1"
            >
              <div
                className={`mb-6 inline-flex p-4 rounded-2xl ${feature.color} group-hover:scale-110 transition-transform duration-300`}
              >
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-900 group-hover:text-primary transition-colors duration-300">
                {t(feature.titleKey)}
              </h3>
              <p className="text-slate-600 leading-relaxed font-medium text-opacity-80 hover:text-opacity-100 transition-opacity">
                 {t(feature.descriptionKey)}
              </p> 
            </div>
          ))}
        </div>

        <div className="mt-16 text-center md:hidden">
          <a
            href="#pricing"
            className="inline-flex items-center px-8 py-3 bg-secondary text-white font-bold rounded-xl hover:bg-secondary-dark transition-colors duration-300 shadow-lg shadow-secondary/20"
          >
            {t("features_button_seePricing")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
